import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom';
import { BarChart3, TrendingUp, TrendingDown, ArrowUp, ArrowDown, ArrowRight, Activity, AlertTriangle } from 'lucide-react';
import { ForexTicker, SYMBOL_ORDER, TV_MAP } from '@/components/market/ForexTicker';
import { Sparkline } from '@/components/market/Sparkline';
import { useMarketPrices } from '@/hooks/useMarketPrices';
import { useMacroIndicators } from '@/hooks/useMacroIndicators';
import { useSymbolRegistry } from '@/hooks/useSymbolRegistry';
import { useMarketRegimes } from '@/hooks/useMarketRegimes';
import { useCorrelations } from '@/hooks/useCorrelations';
import { getCurrentMarketSessions } from '@/utils/marketSessions';
import { motion, AnimatePresence } from 'framer-motion';

/* ─── Bloomberg Macro Card ─── */
function BloombergMacroCard({ label, sublabel, value, change, formatter, statusColor }: {
  label: string;
  sublabel: string;
  value: number | undefined;
  change?: number | null;
  formatter: (v: number) => string;
  statusColor?: string;
}) {
  if (value === undefined) return null;
  return (
    <div className="flex flex-col items-center gap-1 min-w-[140px] px-4 py-3 rounded-lg bg-[hsl(var(--background-secondary))] border border-[hsl(var(--card-border))]">
      <div className="flex items-center gap-1.5">
        {statusColor && <div className={`w-2 h-2 rounded-full ${statusColor}`} />}
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{sublabel}</span>
      </div>
      <span className="text-lg font-mono-data font-bold text-foreground">{formatter(value)}</span>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      {change !== null && change !== undefined && (
        <span className={`text-[10px] font-mono-data flex items-center gap-0.5 ${change >= 0 ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>
          {change >= 0 ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
          {change >= 0 ? '+' : ''}{change.toFixed(2)}%
        </span>
      )}
    </div>
  );
}

/* ─── Fear & Greed Bar ─── */
function FearGreedCard({ value }: { value: number | undefined }) {
  if (value === undefined) return null;
  const label = value < 25 ? 'Extreme Fear' : value < 45 ? 'Fear' : value < 55 ? 'Neutral' : value < 75 ? 'Greed' : 'Extreme Greed';
  return (
    <div className="flex flex-col items-center gap-1 min-w-[160px] px-4 py-3 rounded-lg bg-[hsl(var(--background-secondary))] border border-[hsl(var(--card-border))]">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Sentiment</span>
      <span className="text-lg font-mono-data font-bold text-foreground">{Math.round(value)}</span>
      <div className="w-full h-1.5 rounded-full bg-[hsl(var(--background-tertiary))] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${value}%`,
            background: `linear-gradient(90deg, hsl(var(--bear)), hsl(var(--warning)), hsl(var(--bull)))`,
          }}
        />
      </div>
      <span className="text-[9px] text-muted-foreground">{label}</span>
    </div>
  );
}

/* ─── Regime Badge ─── */
function RegimeBadge({ regime }: { regime: string }) {
  switch (regime) {
    case 'trending':
      return (
        <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-[hsl(var(--accent-blue)/0.15)] text-[hsl(var(--accent-blue))] gap-0.5">
          <TrendingUp className="w-2.5 h-2.5" />Trend
        </Badge>
      );
    case 'volatile':
      return (
        <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-[hsl(var(--accent-amber)/0.15)] text-[hsl(var(--accent-amber))] gap-0.5">
          <Activity className="w-2.5 h-2.5" />Volatile
        </Badge>
      );
    case 'crisis':
      return (
        <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-[hsl(var(--bear))]/15 text-[hsl(var(--bear))] gap-0.5">
          <AlertTriangle className="w-2.5 h-2.5" />Crisis
        </Badge>
      );
    case 'ranging':
      return (
        <Badge variant="secondary" className="text-[9px] px-1.5 py-0 bg-muted text-muted-foreground gap-0.5">
          <ArrowRight className="w-2.5 h-2.5" />Range
        </Badge>
      );
    default:
      return null;
  }
}

/* ─── Enhanced Price Card with Sparkline ─── */
function PriceCard({ symbol, displayName, price, changePercent, regime, onClick }: {
  symbol: string;
  displayName: string;
  price: number;
  changePercent: number;
  regime?: string | null;
  onClick: () => void;
}) {
  const isUp = changePercent >= 0;
  const decimals = price > 100 ? 2 : price > 1 ? 4 : 5;
  // Generate synthetic sparkline data from price
  const sparkData = useMemo(() => {
    const base = price;
    const variance = base * 0.003;
    return Array.from({ length: 24 }, (_, i) => {
      const noise = Math.sin(i * 0.5 + symbol.charCodeAt(0)) * variance;
      const trend = isUp ? (i / 24) * variance : -(i / 24) * variance;
      return base + noise + trend;
    });
  }, [price, symbol, isUp]);

  return (
    <Card
      className="group bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))] hover:border-primary/40 cursor-pointer transition-all duration-200 relative overflow-hidden"
      onClick={onClick}
    >
      <CardContent className="p-3">
        {/* Regime badge top-right */}
        {regime && (
          <div className="absolute top-2 right-2">
            <RegimeBadge regime={regime} />
          </div>
        )}
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-sm font-bold text-foreground">{symbol}</p>
            <p className="text-[10px] text-muted-foreground truncate max-w-[100px]">{displayName}</p>
          </div>
        </div>
        <div className="flex items-end justify-between gap-2">
          <div>
            <p className="text-lg font-mono-data font-semibold text-foreground">
              {price > 100 ? '$' : ''}{price.toFixed(decimals)}
            </p>
            <span className={`text-xs font-mono-data ${isUp ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>
              {isUp ? '+' : ''}{changePercent.toFixed(2)}%
            </span>
          </div>
          <Sparkline data={sparkData} width={50} height={20} positive={isUp} />
        </div>
        {/* Hover Trade overlay */}
        <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
          <span className="text-xs font-semibold text-primary bg-[hsl(var(--background-secondary)/0.9)] px-3 py-1 rounded-md">
            Trade →
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── VIX status color ─── */
function vixStatusColor(v: number): string {
  if (v < 15) return 'bg-[hsl(var(--bull))]';
  if (v <= 25) return 'bg-[hsl(var(--warning))]';
  return 'bg-[hsl(var(--bear))]';
}

/* ─── Correlation Heatmap ─── */
const HEATMAP_SYMBOLS = ['EUR/USD', 'XAU/USD', 'BTC/USD', 'SPY', 'DXY', 'US10Y'];

function CorrelationHeatmap({ correlations }: { correlations: { symbol_a: string; symbol_b: string; correlation: number }[] }) {
  const getCorr = (a: string, b: string): number | null => {
    if (a === b) return 1;
    const [sa, sb] = [a, b].sort();
    const found = correlations.find(c => c.symbol_a === sa && c.symbol_b === sb);
    return found ? found.correlation : null;
  };

  const getCellColor = (v: number | null): string => {
    if (v === null) return 'bg-muted/20';
    if (v > 0.7) return 'bg-[hsl(var(--bull)/0.5)]';
    if (v > 0.3) return 'bg-[hsl(var(--bull)/0.2)]';
    if (v > -0.3) return 'bg-muted/30';
    if (v > -0.7) return 'bg-[hsl(var(--bear)/0.2)]';
    return 'bg-[hsl(var(--bear)/0.5)]';
  };

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
        <table className="border-collapse">
          <thead>
            <tr>
              <th className="w-16" />
              {HEATMAP_SYMBOLS.map(s => (
                <th key={s} className="text-[9px] font-mono-data text-muted-foreground font-normal px-1 py-1 text-center w-14">
                  {s.replace('/USD', '').replace('/', '')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HEATMAP_SYMBOLS.map(row => (
              <tr key={row}>
                <td className="text-[9px] font-mono-data text-muted-foreground pr-2 text-right">
                  {row.replace('/USD', '').replace('/', '')}
                </td>
                {HEATMAP_SYMBOLS.map(col => {
                  const v = getCorr(row, col);
                  return (
                    <Tooltip key={col}>
                      <TooltipTrigger asChild>
                        <td className={`w-14 h-8 text-center text-[10px] font-mono-data cursor-default border border-[hsl(var(--background))] rounded-sm ${getCellColor(v)}`}>
                          {v !== null ? v.toFixed(2) : '—'}
                        </td>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs">
                        {row} ↔ {col}: {v !== null ? v.toFixed(3) : 'N/A'}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </TooltipProvider>
  );
}

const Market = () => {
  const navigate = useNavigate();
  const { prices, gainers, losers, isLoading, lastUpdate } = useMarketPrices(5000);
  const { data: macros, isLoading: macrosLoading } = useMacroIndicators();
  const { data: symbols } = useSymbolRegistry();
  const { correlations, isLoading: corrLoading } = useCorrelations();
  const { getRegime } = useMarketRegimes();
  const [sessions, setSessions] = useState(getCurrentMarketSessions());
  const [selectedSymbol, setSelectedSymbol] = useState('EUR/USD');
  const [activeTab, setActiveTab] = useState('all');

  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSessions(getCurrentMarketSessions()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
    const keyMap: Record<string, number> = {
      '1': 0, '2': 1, '3': 2, '4': 3, '5': 4, '6': 5,
      '7': 6, '8': 7, '9': 8, '0': 9, '-': 10, '=': 11,
    };
    const idx = keyMap[e.key];
    if (idx !== undefined && idx < SYMBOL_ORDER.length) {
      const symbol = SYMBOL_ORDER[idx];
      const tvSymbol = TV_MAP[symbol];
      if (tvSymbol) navigate(`/trading-zone?symbol=${encodeURIComponent(tvSymbol)}`);
    }
  }, [navigate]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Macro lookups
  const macroMap = useMemo(() => {
    const m: Record<string, { value: number; change: number | null }> = {};
    (macros ?? []).forEach(i => { m[i.indicator_name] = { value: i.value, change: i.change_percent }; });
    return m;
  }, [macros]);

  // Symbol registry lookup
  const symbolMap = useMemo(() => {
    const m: Record<string, { display_name: string; asset_class: string }> = {};
    (symbols ?? []).forEach(s => { m[s.symbol] = { display_name: s.display_name, asset_class: s.asset_class }; });
    return m;
  }, [symbols]);

  // Merge prices with symbol registry
  const enrichedPrices = useMemo(() =>
    prices.map(p => ({
      ...p,
      display_name: symbolMap[p.symbol]?.display_name ?? p.symbol,
      asset_class: symbolMap[p.symbol]?.asset_class ?? 'other',
    })),
    [prices, symbolMap]
  );

  // Filter by tab
  const filteredPrices = useMemo(() => {
    if (activeTab === 'all') return enrichedPrices;
    return enrichedPrices.filter(p => p.asset_class === activeTab);
  }, [enrichedPrices, activeTab]);

  const isLive = prices.length > 0;
  const isStale = !lastUpdate || (Date.now() - lastUpdate.getTime() > 60_000);
  const secondsAgo = lastUpdate ? Math.round((Date.now() - lastUpdate.getTime()) / 1000) : null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[hsl(var(--background-secondary))] rounded-lg px-4 py-3 border border-[hsl(var(--card-border))]">
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${isLive && !isStale ? 'bg-[hsl(var(--bull))] animate-pulse' : 'bg-[hsl(var(--warning))]'}`} />
          <span className="font-medium text-foreground">{isLive && !isStale ? 'Live' : 'Delayed'}</span>
          {secondsAgo !== null && <span className="text-muted-foreground">• Synced {secondsAgo}s ago</span>}
        </div>
        <Button size="sm" onClick={() => navigate('/trading-zone')} className="gap-2">
          <BarChart3 className="w-4 h-4" />
          Trading Zone
        </Button>
      </div>

      {/* ─── Bloomberg Macro Dashboard Bar ─── */}
      <div className="flex items-stretch gap-2 overflow-x-auto pb-1 snap-x snap-mandatory scrollbar-none">
        {macrosLoading ? (
          <>
            <Skeleton className="h-[76px] min-w-[140px] shrink-0 snap-start" />
            <Skeleton className="h-[76px] min-w-[140px] shrink-0 snap-start" />
            <Skeleton className="h-[76px] min-w-[140px] shrink-0 snap-start" />
            <Skeleton className="h-[76px] min-w-[140px] shrink-0 snap-start" />
            <Skeleton className="h-[76px] min-w-[160px] shrink-0 snap-start" />
          </>
        ) : (
          <>
            <div className="shrink-0 snap-start">
              <BloombergMacroCard label="VIX" sublabel="Fear Level" value={macroMap['VIX']?.value} change={macroMap['VIX']?.change} formatter={v => v.toFixed(1)} statusColor={macroMap['VIX']?.value !== undefined ? vixStatusColor(macroMap['VIX'].value) : undefined} />
            </div>
            <div className="shrink-0 snap-start">
              <BloombergMacroCard label="DXY" sublabel="Dollar" value={macroMap['DXY']?.value} change={macroMap['DXY']?.change} formatter={v => v.toFixed(2)} />
            </div>
            <div className="shrink-0 snap-start">
              <BloombergMacroCard label="Fed Rate" sublabel="Rates" value={macroMap['FED_RATE']?.value} formatter={v => v.toFixed(2) + '%'} />
            </div>
            <div className="shrink-0 snap-start">
              <BloombergMacroCard label="10Y Yield" sublabel="Bonds" value={macroMap['US10Y']?.value} change={macroMap['US10Y']?.change} formatter={v => v.toFixed(2) + '%'} />
            </div>
            <div className="shrink-0 snap-start">
              <FearGreedCard value={macroMap['FEAR_GREED']?.value} />
            </div>
            {Object.keys(macroMap).length === 0 && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground px-3 py-2">
                <Activity className="w-3.5 h-3.5" />
                Macro data syncing...
              </div>
            )}
          </>
        )}
      </div>

      {/* Price Grid (original ticker) */}
      <ForexTicker />

      {/* Asset Class Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="forex">Forex</TabsTrigger>
          <TabsTrigger value="crypto">Crypto</TabsTrigger>
          <TabsTrigger value="stock">US Stocks</TabsTrigger>
          <TabsTrigger value="commodity">Commodities</TabsTrigger>
        </TabsList>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <TabsContent value={activeTab} className="mt-4">
              {filteredPrices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No instruments found for this category
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                  {filteredPrices.map(p => (
                    <PriceCard
                      key={p.symbol}
                      symbol={p.symbol}
                      displayName={p.display_name}
                      price={p.price}
                      changePercent={p.change_percent}
                      regime={getRegime(p.symbol)?.regime}
                      onClick={() => navigate(`/trading-zone?symbol=${encodeURIComponent(TV_MAP[p.symbol] || p.symbol)}`)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </motion.div>
        </AnimatePresence>
      </Tabs>

      {/* Three Columns: Sessions / Top Movers / Quick Trade */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Market Sessions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sessions.map(s => (
              <div key={s.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-base">{s.flag}</span>
                  <span className="text-sm font-medium text-foreground">{s.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={s.isOpen ? 'default' : 'outline'} className={`text-xs ${s.isOpen ? 'bg-[hsl(var(--bull))] text-[hsl(var(--success-foreground))]' : ''}`}>
                    {s.isOpen ? 'Open' : 'Closed'}
                  </Badge>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{s.countdown}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Top Movers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-2">Gainers</p>
              {gainers.slice(0, 3).map(p => (
                <div key={p.symbol} className="flex items-center justify-between py-1">
                  <span className="text-sm font-medium text-foreground">{p.symbol}</span>
                  <span className="text-sm font-mono-data text-[hsl(var(--bull))] flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />+{p.change_percent.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-2">Losers</p>
              {losers.slice(0, 3).map(p => (
                <div key={p.symbol} className="flex items-center justify-between py-1">
                  <span className="text-sm font-medium text-foreground">{p.symbol}</span>
                  <span className="text-sm font-mono-data text-[hsl(var(--bear))] flex items-center gap-1">
                    <TrendingDown className="w-3 h-3" />{p.change_percent.toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Quick Trade</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={selectedSymbol} onValueChange={setSelectedSymbol}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(TV_MAP).map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <Button
                className="bg-[hsl(var(--bull))] hover:bg-[hsl(var(--bull))]/90 text-white"
                onClick={() => navigate(`/trading-zone?symbol=${encodeURIComponent(TV_MAP[selectedSymbol])}`)}
              >Buy</Button>
              <Button
                className="bg-[hsl(var(--bear))] hover:bg-[hsl(var(--bear))]/90 text-white"
                onClick={() => navigate(`/trading-zone?symbol=${encodeURIComponent(TV_MAP[selectedSymbol])}`)}
              >Sell</Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">Press 1-9, 0, -, = to quick-navigate</p>
          </CardContent>
        </Card>
      </div>

      {/* ─── Cross-Asset Correlation Heatmap ─── */}
      <Card className="bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Cross-Asset Correlations
            </CardTitle>
            <span className="text-[10px] text-muted-foreground">Updated daily</span>
          </div>
        </CardHeader>
        <CardContent>
          {corrLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : correlations.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <AlertTriangle className="w-4 h-4" />
              Computing correlations… Data appears after 20+ days of price history.
            </div>
          ) : (
            <CorrelationHeatmap correlations={correlations} />
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Market;
